<template>
  <popup-modal ref="popup">
    <h3>Endre properties</h3>
    <p class="mt-3">Terminal: <strong>{{ currentTerminalId }}</strong></p>

    <div class="mb-3">
      <label class="form-label">Terminal-ID</label>
      <input type="number" class="form-control" v-model="newTerminalId">
    </div>

    <div class="mb-3">
      <label class="form-label">Enhetsnavn</label>
      <input type="text" class="form-control" v-model="newDeviceName">
    </div>

    <!-- A kitchen display's role (TastyKDS phase 3, item 19): set here, the screen follows; «Velges på skjermen» leaves
         it to «Skjerm» on the screen itself. -->
    <div v-if="isKds" class="mb-3">
      <label class="form-label">Skjermens rolle</label>
      <select class="form-select" v-model="newKdsRole">
        <option value="">Velges på skjermen</option>
        <option value="ALL">Alle linjer</option>
        <option v-for="station in kdsStations" :key="station.id" :value="station.id">Stasjon: {{ station.name }}</option>
        <option v-if="kdsStations.length > 0" value="EXPO">Ekspo – passet</option>
      </select>
      <small class="text-muted d-block mt-1">{{ kdsRoleHint }}</small>
    </div>

    <div v-if="!isKds" class="mb-3">
      <fieldset>
        <label class="multiple-checkbox-label">
          <input type="checkbox" class="multiple-checkbox" v-model="newUseAsCloudPrinter" />
          Bruk som skriver for bestillinger (CloudPRNT)
        </label>
      </fieldset>
      <small class="text-muted">Terminalen skriver da også ut kvittering/kjøkkenlapp for nye bestillinger.</small>
    </div>

    <!-- What this printer prints, shown only once it IS a printer. Every box starts checked for a
         terminal that has no filter yet, because that is what it prints today — an empty set would read
         as "nothing", and the server reads it as "everything". Both lists must keep at least one
         choice: an order matching no printer's filter is never printed and stays ORDERED, which looks
         exactly like a queue that is down. -->
    <template v-if="newUseAsCloudPrinter && !isKds">
      <div class="mb-3 ps-3 cp-filter">
        <div class="cp-filter__title">Skriv ut bestillinger fra</div>
        <fieldset>
          <label v-for="c in channelOptions" :key="c.key" class="multiple-checkbox-label d-block">
            <input type="checkbox" class="multiple-checkbox" :value="c.key" v-model="newChannels" />
            {{ c.label }}
          </label>
        </fieldset>
      </div>

      <div class="mb-3 ps-3 cp-filter">
        <div class="cp-filter__title">Skriv ut ordretyper</div>
        <fieldset>
          <label v-for="o in orderTypeOptions" :key="o.key" class="multiple-checkbox-label d-block">
            <input type="checkbox" class="multiple-checkbox" :value="o.key" v-model="newOrderTypes" />
            {{ o.label }}
          </label>
        </fieldset>
        <small class="text-muted d-block mt-1">
          En bestilling som ikke passer noen skrivers valg, blir aldri skrevet ut. Har du flere skrivere,
          sjekk at valgene til sammen dekker alt.
        </small>
      </div>
    </template>

    <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

    <div class="btn-panel">
      <button class="bo_btn btn-width" @click="cancel" :disabled="saving">Avbryt</button>
      <button class="bo_btn btn-width ms-3" @click="confirm" :disabled="saving || !newTerminalId || filterIncomplete">
        {{ saving ? 'Lagrer...' : 'Bekreft' }}
      </button>
    </div>
  </popup-modal>
</template>

<script>
import PopupModal from '@/components/other/PopupModal.vue'
import TerminalService from '@/services/terminal/TerminalService'
import LocationService from '@/services/location/LocationService'

export default {
  name: 'ChangeTerminalPropertiesModal',
  components: { PopupModal },
  data() {
    return {
      documentId: '',
      currentTerminalId: '',
      newTerminalId: '',
      newDeviceName: '',
      newUseAsCloudPrinter: false,
      newChannels: [],
      newOrderTypes: [],
      errorMessage: '',
      saving: false,
      isKds: false,
      newKdsRole: '',
      kdsStations: [],
      // The same words the orders page uses for these, so a printer configured here matches what the
      // admin sees on /orders. The ordering-app channels (APP_IOS/APP_ANDROID) exist server-side but
      // are not offered — there is no app yet.
      channelOptions: [
        { key: 'WEBSHOP', label: 'Nettbutikk' },
        { key: 'POS', label: 'Kasse' },
        { key: 'KIOSK', label: 'Selvbetjeningskiosk' }
      ],
      orderTypeOptions: [
        { key: 'DINE_IN', label: 'Spise her' },
        { key: 'PICKUP', label: 'Ta med' },
        { key: 'DELIVERY', label: 'Levere' }
      ]
    }
  },
  computed: {
    /** A printer with nothing selected in either list would print nothing; refuse to save that. */
    filterIncomplete() {
      return !this.isKds && this.newUseAsCloudPrinter && (this.newChannels.length === 0 || this.newOrderTypes.length === 0)
    },
    kdsRoleHint() {
      switch (this.newKdsRole) {
        case '': return 'Den som står ved skjermen velger under «Skjerm».'
        case 'ALL': return 'Viser alle bestillinger og alle linjer.'
        case 'EXPO': return 'Passet: alle bestillingene med fremdrift per stasjon, og siste Ferdig.'
        default: return 'Viser bare bestillingene og linjene denne stasjonen lager.'
      }
    }
  },
  methods: {
    open(documentId, currentTerminalId, currentDeviceName, currentUseAsCloudPrinter, channels, orderTypes,
         terminalType, locationId, kdsRole) {
      this.documentId = documentId
      this.currentTerminalId = currentTerminalId
      this.newTerminalId = currentTerminalId
      this.newDeviceName = currentDeviceName || ''
      this.newUseAsCloudPrinter = !!currentUseAsCloudPrinter
      // No filter stored (a terminal from before the field existed) = prints everything, so show it as
      // everything checked rather than as an empty form that would be saved as "nothing".
      const storedChannels = Array.isArray(channels) ? channels : []
      const storedTypes = Array.isArray(orderTypes) ? orderTypes : []
      const known = this.channelOptions.map(c => c.key)
      this.newChannels = storedChannels.length ? storedChannels.filter(c => known.includes(c)) : known
      this.newOrderTypes = storedTypes.length ? storedTypes.slice() : this.orderTypeOptions.map(o => o.key)
      this.errorMessage = ''
      this.saving = false
      this.isKds = terminalType === 'KDS'
      this.newKdsRole = kdsRole || ''
      this.kdsStations = []
      if (this.isKds && locationId) {
        // The stations of the screen's own location — the only ones it can be.
        LocationService.getLocation(locationId).then(
          (response) => {
            const stations = response.data && response.data.kdsSetting && Array.isArray(response.data.kdsSetting.stations)
              ? response.data.kdsSetting.stations : []
            this.kdsStations = stations.filter(station => station && station.id && station.name)
          },
          (error) => { console.log(error) }
        )
      }
      this.$refs.popup.open()
    },
    cancel() {
      this.$refs.popup.close()
    },
    confirm() {
      this.errorMessage = ''
      this.saving = true
      // Not a printer → no filter stored, so turning the printer on again later starts from "everything".
      const channels = this.newUseAsCloudPrinter ? this.newChannels.slice() : []
      const orderTypes = this.newUseAsCloudPrinter ? this.newOrderTypes.slice() : []
      TerminalService.updateTerminalProperties(this.documentId, parseInt(this.newTerminalId), this.newDeviceName,
          this.newUseAsCloudPrinter, channels, orderTypes, this.isKds ? this.newKdsRole : undefined).then(
        () => {
          this.saving = false
          this.$refs.popup.close()
          this.$emit('changed')
        },
        (error) => {
          this.saving = false
          this.errorMessage = error.response?.data?.message || 'Endring feilet. Prøv igjen.'
        }
      )
    }
  }
}
</script>

<style scoped>
/* Align the checkbox with the form fields above — the global .multiple-checkbox indents 20px. */
.multiple-checkbox {
  margin-left: 0;
}

.cp-filter__title {
  font-weight: 600;
  margin-bottom: 4px;
}

.btn-panel {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  border-top: var(--bo-muted-2) solid 1px;
  padding-top: 15px;
}

.btn-width {
  width: 80px;
  text-align: center;
}
</style>
